import { Link } from "wouter";
import { Card } from "../components/Card";
import { EmptyState } from "../components/EmptyState";
import { Skeleton } from "../components/Spinner";
import { TodayView } from "../features/clockin/TodayView";
import { useFeatures } from "../lib/useFeatures";
import { useMe } from "../lib/useMe";
import styles from "./HomePage.module.css";

const CORE_HANDLINGER = [
  {
    href: "/medarbejdere",
    label: "Medarbejdere",
    description: "Opret, invitér og administrér medarbejdere.",
  },
  {
    href: "/indstillinger",
    label: "Indstillinger",
    description: "Tilpas arbejdstid, projekter og virksomhed.",
  },
] as const;

export function HomePage() {
  const { me, loading: meLoading, error: meError } = useMe();
  const { features, loading: featuresLoading, error: featuresError } =
    useFeatures();
  const loading = meLoading || featuresLoading;
  const failed = meError === "failed" || featuresError === "failed";
  const clockinFeature = features.find((feature) => feature.key === "clockin");
  const clockinEnabled = clockinFeature !== undefined;
  const hurtigeHandlinger = [
    ...(clockinFeature === undefined
      ? []
      : [
          {
            href: `/${clockinFeature.key}`,
            label: clockinFeature.name,
            description: "Se dagen, godkend uger og hent rapporter.",
          },
        ]),
    ...CORE_HANDLINGER,
  ];

  return (
    <div className={styles.page}>
      <header className={styles.intro}>
        {loading ? (
          <>
            <Skeleton width="9rem" height="0.875rem" />
            <Skeleton width="18rem" height="2.25rem" />
          </>
        ) : (
          <>
            <p className={styles.tenant}>{me?.tenantNavn ?? "Dagens overblik"}</p>
            <h1 className={styles.title}>
              {me === null ? "Dagens overblik" : `God arbejdsdag, ${me.navn}`}
            </h1>
            <p className={styles.subtitle}>
              Status og de vigtigste genveje samlet ét sted.
            </p>
          </>
        )}
      </header>

      <nav className={styles.quickActions} aria-label="Hurtige handlinger">
        {hurtigeHandlinger.map((handling) => (
          <Link key={handling.href} href={handling.href} className={styles.quickAction}>
            <span className={styles.quickLabel}>{handling.label}</span>
            <span className={styles.quickDescription}>{handling.description}</span>
            <span className={styles.quickArrow} aria-hidden="true">
              →
            </span>
          </Link>
        ))}
      </nav>

      <Card
        title="I dag"
        actions={
          clockinEnabled ? (
            <Link href="/clockin" className={styles.detailLink}>
              Åbn tidsregistrering
            </Link>
          ) : undefined
        }
      >
        {loading ? (
          <div className={styles.loadingRows} aria-label="Henter dagens status">
            <Skeleton height="3.5rem" />
            <Skeleton height="3.5rem" />
          </div>
        ) : failed ? (
          <EmptyState
            title="Overblikket kunne ikke hentes"
            description="Du kan stadig bruge genvejene ovenfor. Prøv at genindlæse siden om lidt."
          />
        ) : clockinEnabled ? (
          <TodayView />
        ) : (
          <EmptyState
            title="Tidsregistrering er slået fra"
            description="Slå den til under Indstillinger, når virksomheden er klar til at registrere tid."
            cta={
              <Link href="/indstillinger" className={styles.emptyLink}>
                Åbn Indstillinger
              </Link>
            }
          />
        )}
      </Card>
    </div>
  );
}
